/**
 * The Ory client.
 *
 * Backend CLAUDE.md §6: "**We do not build authentication.** Identity,
 * credentials, sessions, MFA enrolment, password reset and recovery are Ory's."
 * Nothing in this directory implements any of those. It renders what Ory asks
 * for and posts back what the user typed.
 *
 * §6 again, and it is the rule most easily broken from a frontend: "**Ory
 * authenticates; it never authorizes.** Every money-adjacent instruction still
 * goes through `authz.Authorize`. An Ory session proves *who*, never *may
 * they*." There is deliberately no role, permission or entitlement anywhere in
 * this directory. If a screen needs to know whether something is allowed, it
 * asks the backend and reads the answer — it does not infer one from a session.
 */

import { Configuration, FrontendApi } from '@ory/client-fetch'
import type { AppConfig } from '@/lib/config'

/**
 * Build the frontend API client for this deployment.
 *
 * The base path comes from runtime config, never from a build constant and
 * never from `location.hostname` — same rule as the API base, and
 * `check-invariants.sh` fails the build on either.
 */
export function oryClient(config: AppConfig): FrontendApi {
  return new FrontendApi(
    new Configuration({
      basePath: config.orySdkUrl,
      // Ory's session and CSRF cookies must be sent and accepted. Without this
      // every flow starts fresh and the CSRF check fails on submit.
      credentials: 'include',
      // WITHOUT THIS, NOBODY CAN SIGN IN. Not sign up, not recover, not verify.
      //
      // Ory serves a self-service flow init two ways and picks by Accept. With
      // no header it does the browser thing — 303 to an HTML page — and `fetch`
      // follows the redirect, so `res.json()` gets `<!DOCTYPE` and throws. The
      // app reports that honestly as "the identity provider is unreachable",
      // which is why it reads as an outage rather than as a client bug.
      //
      //   no Accept              -> 303  text/html
      //   Accept: application/json -> 200  application/json  {"id":…,"ui":{…}}
      //
      // `@ory/client-fetch` does not set it: `headerParameters['Accept']`
      // appears zero times in its FrontendApi. Verified against the version in
      // node_modules rather than against the docs (#36).
      headers: { Accept: 'application/json' },
    }),
  )
}

/**
 * Whether Ory is same-site with this app, and therefore whether its cookies
 * will actually survive a modern browser.
 *
 * This is not a nicety. Ory Network's default tenant is
 * `<slug>.projects.oryapis.com`, and a session cookie set there is
 * **third-party** to `tesora.money`. Chrome and Safari drop it, so login
 * appears to succeed and the very next request is anonymous — a failure that
 * looks like "the session did not stick" and is really "the browser refused to
 * store it".
 *
 * The fix is for the **backend to proxy Ory's self-service surface** under its
 * own origin (`go-wallet#271`), so this app talks to
 * `https://bck.<env>.tesora.money/auth` instead. `staging.tesora.money` and
 * `bck.staging.tesora.money` share the registrable domain `tesora.money`, so
 * requests between them are same-site and a host-only `SameSite=Lax` cookie is
 * sent on them.
 *
 * That is a backend change plus a ConfigMap value, and **nothing in `src/`
 * changes** — the base path already comes from `/config.json`. An Ory custom
 * domain would also work and was the first thing considered; it was rejected
 * because it needs an Ory plan feature and a DNS record to solve a problem the
 * service that already holds the Ory client is better placed to solve.
 *
 * Until that lands, this predicate lets the UI say what is wrong instead of
 * leaving someone to debug an empty session. It stays afterwards as a guard
 * against the base path being pointed back at `oryapis.com`.
 */
export function isSameSiteWithOry(config: AppConfig, appOrigin: string): boolean {
  let ory: URL
  let app: URL
  try {
    ory = new URL(config.orySdkUrl)
    app = new URL(appOrigin)
  } catch {
    return false
  }
  return registrableSuffix(ory.hostname) === registrableSuffix(app.hostname)
}

/**
 * The last two labels of a hostname.
 *
 * A deliberate approximation of the public-suffix list, and adequate here
 * because it is only ever asked about our own deployment hostnames — all of
 * which are `*.tesora.money`, plus `localhost` in development. It is used to
 * produce a warning, never to make a security decision, so being wrong about
 * `foo.co.uk` costs nothing.
 */
function registrableSuffix(hostname: string): string {
  const parts = hostname.split('.')
  return parts.length <= 2 ? hostname : parts.slice(-2).join('.')
}
